import { useMemo } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';

import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import type { FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

// Derived from the preset API so we don't take a direct dependency on the AI-suggestions
// editor plugin's exported types.
type AiSuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

type UsePreviewSuggestionResult = {
	/**
	 * The full-page editor preset API, forwarded so callers can pass it to
	 * `PreviewSuggestionButton`. `undefined` outside the full-page editor (e.g. renderer /
	 * view mode), which is why the button naturally does nothing on those surfaces.
	 */
	editorApi: FullPageEditorPresetAPI | undefined;
	/**
	 * Id of the suggestion that was generated from the comment thread identified by the
	 * `markerRef` passed in — i.e. the suggestion whose `sources` reference that marker ref.
	 * `undefined` when this thread has no suggestion. Reactive: updates as suggestions are
	 * added/removed.
	 */
	suggestionId: string | undefined;
};

const PAIR_SEPARATOR = '|';
const KEY_SEPARATOR = ':';

/**
 * Flattens the marker ref → suggestion id relationship encoded in each suggestion's `sources`
 * into a string of `<markerRef>:<suggestionId>` pairs, in document order.
 *
 * A string (rather than a `Map` / object) so that `useSharedPluginStateWithSelector`'s shallow
 * comparison of selector results can tell "the mapping is unchanged" from "the mapping
 * changed" — a fresh collection would compare unequal on every shared-state change (selection,
 * thinking status, …) and re-render every comment thread on the page.
 */
const serializeSuggestionIdsByMarkerRef = (state: AiSuggestionsSharedState): string => {
	if (!state) {
		return '';
	}

	const pairs: string[] = [];
	const seenMarkerRefs = new Set<string>();

	for (const suggestionId of state.orderedSuggestionIds ?? []) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- both collections are small: a document holds a handful of suggestions, each citing a handful of sources.
		for (const source of state.suggestionById?.get(suggestionId)?.sources ?? []) {
			const markerRef = source?.metadata?.markerRef;
			// `metadata` is an untyped bag, so narrow before trusting it. The first suggestion in
			// document order wins when several reference the same thread.
			if (typeof markerRef !== 'string' || !markerRef || seenMarkerRefs.has(markerRef)) {
				continue;
			}
			seenMarkerRefs.add(markerRef);
			pairs.push(`${markerRef}${KEY_SEPARATOR}${suggestionId}`);
		}
	}

	return pairs.join(PAIR_SEPARATOR);
};

/**
 * Reactive access to the editor's AI-suggestions shared state for inline-comment UIs that
 * render {@link PreviewSuggestionButton}.
 *
 * Reads the AI-suggestions plugin's shared state via the editor's
 * `useSharedPluginStateWithSelector`, which subscribes to the plugin store and re-renders on
 * change (note: it is debounced). Do not read `sharedState.currentState()` in render — that
 * is a one-shot snapshot and will not re-render when suggestions change.
 *
 * @param markerRef The inline-comment marker ref (annotation id) of the thread the caller is
 * rendering. A suggestion is considered to belong to that thread when one of its `sources` has
 * `metadata.markerRef` equal to this value. Pass `undefined` for surfaces without a marker ref
 * (e.g. a general comment) — no suggestion will match.
 *
 * Note the marker-ref match is deliberately done outside the selector:
 * `useSharedPluginStateWithSelector` captures the selector on mount, so a selector closing over
 * `markerRef` would keep matching against the value it had on first render.
 */
export function usePreviewSuggestion(markerRef?: string): UsePreviewSuggestionResult {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	const { suggestionIdsByMarkerRef } = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		(state: { aiSuggestionsState?: AiSuggestionsSharedState }) => ({
			suggestionIdsByMarkerRef: serializeSuggestionIdsByMarkerRef(state?.aiSuggestionsState),
		}),
	);

	const suggestionId = useMemo(() => {
		if (!markerRef || !suggestionIdsByMarkerRef) {
			return undefined;
		}
		const prefix = `${markerRef}${KEY_SEPARATOR}`;
		const pair = suggestionIdsByMarkerRef
			.split(PAIR_SEPARATOR)
			.find((candidate) => candidate.startsWith(prefix));
		return pair?.slice(prefix.length);
	}, [markerRef, suggestionIdsByMarkerRef]);

	return { editorApi: fullPageEditorPresetAPI, suggestionId };
}
